// Pure resolution logic for the review screen: initial defaults, the EB->CB dependency that makes
// orphan-creating selections impossible, bulk operations, and mapping to the API payload. Kept free
// of React so the rules are easy to read and reason about.

import {
  GabRowResolutionType,
  RowDiff,
  RowResolution,
  SyncSession,
  rowKey,
} from "./types";

export type ResolutionMap = Record<string, GabRowResolutionType>;

// A row is actionable only when it is applicable (no blocking conflict) and actually differs
// (New or Changed). Unchanged and conflicted rows are shown but never resolvable.
export function isResolvable(row: RowDiff): boolean {
  return row.isApplicable && row.hasChanges;
}

// New rows default to TakeSource (pre-selected inserts); Changed rows default to Keep, so
// overwriting existing target data is always a deliberate opt-in.
export function initialResolutions(session: SyncSession): ResolutionMap {
  const map: ResolutionMap = {};
  for (const table of session.tables) {
    for (const row of table.rows) {
      if (!isResolvable(row)) continue;
      map[rowKey(row.tableCode, row.keyValue)] =
        row.changeType === "New" ? "TakeSource" : "Keep";
    }
  }
  return map;
}

// child row key -> parent (CB) row key, only when the parent is itself a resolvable row in the
// session. This is the dependency edge the cascade below protects.
export function parentLinks(session: SyncSession): Record<string, string> {
  const cbByKey: Record<string, RowDiff> = {};
  const children: RowDiff[] = [];
  for (const table of session.tables) {
    for (const row of table.rows) {
      if (row.tableCode === "CB") cbByKey[row.keyValue ?? ""] = row;
      if (row.tableCode === "EB") children.push(row);
    }
  }

  const links: Record<string, string> = {};
  for (const child of children) {
    if (!isResolvable(child)) continue;
    const parentDtc = child.sourcePayload["ParentCompDtc"];
    if (parentDtc == null) continue;
    const parent = cbByKey[parentDtc];
    if (parent && isResolvable(parent)) {
      links[rowKey(child.tableCode, child.keyValue)] =
        rowKey(parent.tableCode, parent.keyValue);
    }
  }
  return links;
}

// Apply one resolution, cascading to keep parent/child selections consistent:
//  - selecting a child (TakeSource) also selects its parent, so the child never applies alone;
//  - keeping a parent (Keep) forces any selected children back to Keep, so none is orphaned.
export function applyResolution(
  map: ResolutionMap,
  links: Record<string, string>,
  key: string,
  value: GabRowResolutionType
): ResolutionMap {
  const next: ResolutionMap = { ...map, [key]: value };

  // Anything that applies the row (TakeSource or Custom) requires its parent to apply too.
  if (value !== "Keep") {
    const parentKey = links[key];
    if (parentKey) next[parentKey] = "TakeSource";
  } else {
    for (const [childKey, parentKey] of Object.entries(links)) {
      if (parentKey === key && next[childKey] !== "Keep") {
        next[childKey] = "Keep";
      }
    }
  }
  return next;
}

export type BulkTarget = "new" | "changed" | "all";

// Bulk-set every resolvable row of a table that matches the target, applying the same cascade so
// bulk actions can't create orphans either.
export function bulkResolve(
  map: ResolutionMap,
  links: Record<string, string>,
  session: SyncSession,
  tableCode: string,
  target: BulkTarget,
  value: GabRowResolutionType
): ResolutionMap {
  let next = map;
  const table = session.tables.find((t) => t.tableCode === tableCode);
  if (!table) return next;

  for (const row of table.rows) {
    if (!isResolvable(row)) continue;
    if (target === "new" && row.changeType !== "New") continue;
    if (target === "changed" && row.changeType !== "Changed") continue;
    next = applyResolution(next, links, rowKey(row.tableCode, row.keyValue), value);
  }
  return next;
}

export type CustomPayloadMap = Record<string, Record<string, string | null>>;

// Full resolution list for submission — one entry per resolvable row, carrying the hand-edited
// payload for Custom rows.
export function toResolutionList(
  session: SyncSession,
  map: ResolutionMap,
  customPayloads: CustomPayloadMap
): RowResolution[] {
  const out: RowResolution[] = [];
  for (const table of session.tables) {
    for (const row of table.rows) {
      if (!isResolvable(row)) continue;
      const k = rowKey(row.tableCode, row.keyValue);
      const resolution = map[k] ?? "Keep";
      out.push({
        tableCode: row.tableCode,
        keyValue: row.keyValue,
        resolution,
        customPayload: resolution === "Custom" ? customPayloads[k] : undefined,
      });
    }
  }
  return out;
}

export interface TableResolutionSummary {
  tableCode: string;
  tableName: string;
  insert: number; // New, applied (TakeSource or Custom)
  overwrite: number; // Changed, applied (TakeSource or Custom)
  custom: number; // applied rows composed in the editor
  keep: number; // resolvable rows left as Keep
  conflicts: number; // non-applicable rows (skipped)
}

export function summarize(session: SyncSession, map: ResolutionMap): TableResolutionSummary[] {
  return session.tables.map((table) => {
    let insert = 0;
    let overwrite = 0;
    let custom = 0;
    let keep = 0;
    for (const row of table.rows) {
      if (!isResolvable(row)) continue;
      const choice = map[rowKey(row.tableCode, row.keyValue)] ?? "Keep";
      if (choice !== "Keep") {
        if (row.changeType === "New") insert += 1;
        else overwrite += 1;
        if (choice === "Custom") custom += 1;
      } else {
        keep += 1;
      }
    }
    return {
      tableCode: table.tableCode,
      tableName: table.tableName,
      insert,
      overwrite,
      custom,
      keep,
      conflicts: table.conflictCount,
    };
  });
}
